import { JobStatusUtils, JobStatusV4 } from '@core/models/optimization.model';
import { PlanMetadataModel } from '@core/models/plan.model';

import { isNil } from './shared-utils';

/**
 * Whether the backend would refuse a write to this plan right now, mirroring GBE's
 * `checkCanEditOrThrow`. Shared by every entry point that leads to a write — the plan
 * toolbox, and the entity table's create and CSV-import actions — so the affordance is
 * disabled before the user fills in a form or prepares a file, rather than after the
 * request comes back rejected.
 *
 * A blocking finding is deliberately not a reason: editing is often the only way to fix
 * one (e.g. adding the missing entity), so findings gate optimizing, not writing.
 *
 * `isDeployed` is not a reason either: it is set the moment a deploy *attempt* opens and
 * never cleared, so a deploy refused for a missing driver phone would freeze the plan
 * against the very edit its repair list asks for. The conditions below mirror the refusals
 * GBE does still raise — `plan_already_running` and `plan_already_optimized`.
 */
export function isPlanEditBlocked(plan: PlanMetadataModel): boolean {
  return (plan.childrenInfo ?? []).some(
    (child) =>
      JobStatusUtils.isUnfinishedStatus(child.jobStatus) ||
      (child.jobStatus === JobStatusV4.OPTIMIZATION_FINISHED && !isNil(child.childPlanId))
  );
}
